/**
 * The offline banner (06-flows.md F10 step 1: "an offline banner with pending count"), the
 * list of saved changes the server refused, and the notices (a bill the server priced
 * differently). Always present on the signed-in frame; silent when everything is in sync.
 */
import { Alert, Badge, Button, Dialog, formatMoney } from '@qrkey/ui';
import { Link } from '@tanstack/react-router';
import { useState } from 'react';
import { dismissNotice, dismissProblem, syncNow } from './sync.js';
import { useOfflineStatus } from './hooks.js';
import type { QueuedCommand } from './db.js';

const REASON: Record<string, string> = {
  VERSION_CONFLICT: 'Someone changed this order while the till was offline.',
  TABLE_OCCUPIED: 'The table was taken by another order.',
  ORDER_BILLED: 'The order had already been billed.',
  ORDER_ALREADY_SETTLED: 'The order had already been settled.',
  ORDER_CANCELLED: 'The order had already been cancelled.',
  OUT_OF_STOCK: 'An item was sold out by the time this reached the server.',
  FORBIDDEN: 'Your role is not allowed to do this.',
  INVOICE_BLOCK_INVALID: 'The invoice number this till used is not one it was given.',
  INVOICE_BLOCK_CLOSED: 'The invoice number this till used was given back.',
  INVOICE_NUMBER_USED: 'The invoice number was already used.',
  KOT_NUMBER_INVALID: 'The kitchen ticket number was not one this till was given.',
  DEPENDS_ON_REFUSED: 'An earlier change to this order was refused, so this one was not applied.',
  GAVE_UP: 'The server could not apply this after several tries.',
};

function reason(c: QueuedCommand): string {
  return (c.code ? REASON[c.code] : undefined) ?? c.detail ?? 'The server did not accept it.';
}

export function OfflineBanner({ outletId }: { outletId: string | null }) {
  const s = useOfflineStatus(outletId);
  const [open, setOpen] = useState(false);
  const offline = s.connectivity === 'offline';
  const attention = s.problems.length + s.notices.length;
  if (!offline && s.pending === 0 && attention === 0 && !s.localSession && s.sync !== 'blocked') {
    return null;
  }
  const waiting = `${s.pending} ${s.pending === 1 ? 'change' : 'changes'} saved on this till`;
  return (
    <div className="offline-banner" data-testid="offline-banner">
      {offline ? (
        <Alert tone="warning" className="offline-banner__alert">
          <strong data-testid="offline-state">Offline.</strong> Orders, KOTs, bills and cash
          payments carry on and are sent when the connection is back.{' '}
          <span data-testid="pending-count" data-count={s.pending}>
            {waiting}.
          </span>
        </Alert>
      ) : s.sync === 'needs-sign-in' || (s.localSession && s.pending > 0) ? (
        <Alert
          tone="warning"
          className="offline-banner__alert"
          action={
            <Link to="/pin" className="qk-btn qk-btn--secondary qk-btn--md">
              <span className="qk-btn__label">Sign in</span>
            </Link>
          }
        >
          <span data-testid="pending-count" data-count={s.pending}>
            {waiting}.
          </span>{' '}
          Sign in with your PIN to send them.
        </Alert>
      ) : s.sync === 'blocked' ? (
        <Alert tone="danger" className="offline-banner__alert">
          This device was blocked, so {waiting} cannot be sent. Ask a manager.
        </Alert>
      ) : s.pending > 0 ? (
        <Alert
          tone="info"
          className="offline-banner__alert"
          action={
            <Button variant="secondary" onClick={() => void syncNow()}>
              Send now
            </Button>
          }
        >
          <span data-testid="pending-count" data-count={s.pending}>
            Sending {waiting}…
          </span>
        </Alert>
      ) : null}
      {attention > 0 ? (
        <Alert
          tone={s.problems.length > 0 ? 'danger' : 'info'}
          className="offline-banner__alert"
          action={
            <Button variant="secondary" onClick={() => setOpen(true)}>
              Review
            </Button>
          }
        >
          <span data-testid="attention-count" data-count={attention}>
            {s.problems.length > 0
              ? `${s.problems.length} saved ${s.problems.length === 1 ? 'change was' : 'changes were'} not accepted by the server.`
              : `${s.notices.length} ${s.notices.length === 1 ? 'bill was' : 'bills were'} priced differently by the server.`}
          </span>
        </Alert>
      ) : null}
      {open ? <ConflictsDialog outletId={outletId} onClose={() => setOpen(false)} /> : null}
    </div>
  );
}

function ConflictsDialog({ outletId, onClose }: { outletId: string | null; onClose(): void }) {
  const s = useOfflineStatus(outletId);
  return (
    <Dialog open onClose={onClose} title="Saved changes that need a look" size="md">
      <div className="stack" data-testid="conflicts">
        {s.problems.length === 0 && s.notices.length === 0 ? <p>Nothing needs attention.</p> : null}
        {s.problems.length > 0 ? (
          <ul className="conflicts" aria-label="Changes not accepted">
            {s.problems.map((c) => (
              <li key={c.idempotencyKey} className="conflicts__item" data-testid="conflict">
                <div>
                  <strong>{c.summary}</strong>{' '}
                  <Badge tone={c.status === 'conflict' ? 'warning' : 'danger'}>
                    {c.status === 'conflict' ? 'Conflict' : 'Refused'}
                  </Badge>
                  <p>{reason(c)}</p>
                  <small>
                    {c.by.name ? `${c.by.name} · ` : ''}
                    {new Date(c.clientAt).toLocaleTimeString()}
                  </small>
                </div>
                <Button
                  variant="ghost"
                  onClick={() => {
                    if (c.seq !== undefined) void dismissProblem(c.seq);
                  }}
                >
                  Discard
                </Button>
              </li>
            ))}
          </ul>
        ) : null}
        {s.notices.length > 0 ? (
          <ul className="conflicts" aria-label="Bills priced differently">
            {s.notices.map((n) => (
              <li key={n.id} className="conflicts__item" data-testid="notice">
                <div>
                  <strong>Order {n.orderNo ?? ''}</strong>{' '}
                  <Badge tone="info">Priced differently</Badge>
                  <p>
                    The till billed {formatMoney(n.keptTotalPaise)}; the server prices it at{' '}
                    {formatMoney(n.serverTotalPaise)}. The customer&apos;s bill stays as printed; a
                    manager can see the difference.
                  </p>
                </div>
                <Button
                  variant="ghost"
                  onClick={() => {
                    if (outletId) void dismissNotice(outletId, n.id);
                  }}
                >
                  OK
                </Button>
              </li>
            ))}
          </ul>
        ) : null}
      </div>
    </Dialog>
  );
}
